/**
 * The node modal's neighbourhood: beside the (narrower) modal, what leads to the card
 * on the left and what follows from it on the right, from the map graph — the same
 * meaning the scene and pivots read. A neighbour in another town carries the town's
 * name. Tapping a neighbour opens it. The card's source is said in words at the top.
 * Wide screens only; on a narrow one the modal stays as it was.
 */
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { BookOpen, Bot, User, MapPin } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { EFFECT_TYPE_KEYS } from '../../config/effectTypes';
import { neighbourhood, sourceOf } from './neighbourhood';
import useMapGraph from './useMapGraph';

export const NEIGHBOURHOOD_MODAL_WIDTH = '560px';
const MIN_WIDE = 1200;

export function useWideEnough() {
  const query = `(min-width: ${MIN_WIDE}px)`;
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return wide;
}

const Column = styled.aside`
  position: fixed; top: 8vh; bottom: 8vh; z-index: 1002;
  width: min(330px, calc((100vw - ${NEIGHBOURHOOD_MODAL_WIDTH}) / 2 - 40px));
  ${(p) => (p.$side === 'left' ? 'right: calc(50% + 280px + 20px);' : 'left: calc(50% + 280px + 20px);')}
  display: flex; flex-direction: column; gap: 8px; overflow-y: auto; pointer-events: auto;
`;
const Heading = styled.h3`margin: 0 0 2px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #9ca3af;`;
const Source = styled.div`
  padding: 10px 12px; border-radius: 10px; background: rgba(20, 20, 24, 0.92); border: 1px solid rgba(255,255,255,0.10);
  color: #d1d5db; font-size: 0.8rem; line-height: 1.4; display: flex; gap: 8px; align-items: flex-start;
  svg { flex-shrink: 0; margin-top: 2px; }
`;
const Item = styled.button`
  text-align: left; width: 100%; padding: 9px 11px; border-radius: 10px; font: inherit; color: #e5e7eb;
  background: rgba(20, 20, 24, 0.92); border: 1px solid ${(p) => (p.$cross ? 'rgba(120,160,210,0.55)' : 'rgba(255,255,255,0.10)')};
  border-style: ${(p) => (p.$cross ? 'dashed' : 'solid')};
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  &:hover:not(:disabled) { background: rgba(40, 40, 48, 0.95); }
`;
const Word = styled.span`font-size: 0.7rem; font-weight: 700; color: ${(p) => p.$color}; margin-right: 6px;`;
const Town = styled.span`font-size: 0.68rem; color: #93b4dc; display: inline-flex; gap: 3px; align-items: center; margin-left: 6px;`;
const Text = styled.div`font-size: 0.86rem; margin-top: 3px;`;
const Empty = styled.div`font-size: 0.8rem; color: #6b7280; padding: 4px 2px;`;

const SOURCE_ICON = { documents: BookOpen, ai: Bot, person: User };
const VALENCE_COLOR = { wanted: '#34D399', unwanted: '#F87171' };

function Neighbour({ entry, onOpen, t }) {
  const { other } = entry;
  const word = entry.type === 'avoids' ? t('impactPath.neighbourhood.avoids') : (EFFECT_TYPE_KEYS.includes(entry.type) ? t(`impactMapV2.effectTypes.${entry.type}`) : entry.type);
  const Icon = SOURCE_ICON[other.source];
  return (
    <Item type="button" $cross={entry.crossScenario} disabled={!other.uuid} onClick={() => other.uuid && onOpen(other.uuid)}>
      <Word $color={entry.type === 'blocks' ? '#EF5350' : entry.type === 'avoids' ? '#34D399' : '#9ab4d8'}>{word}</Word>
      {Icon && <Icon size={11} color="#9ca3af" />}
      {entry.crossScenario && <Town><MapPin size={10} />{other.scenarioName}</Town>}
      <Text style={{ color: VALENCE_COLOR[other.valence] || undefined }}>
        {other.kind === 'start' ? `${t('impactPath.neighbourhood.start')}: ${other.text}` : other.text}
      </Text>
    </Item>
  );
}

export default function NodeNeighbourhood({ instanceId, nodeId, onOpen }) {
  const { t } = useTranslation();
  const { graph, error } = useMapGraph(instanceId, nodeId);
  const nb = useMemo(() => neighbourhood(graph, nodeId), [graph, nodeId]);
  if (error) return <Column $side="left"><Empty>{t('impactPath.neighbourhood.error')}</Empty></Column>;
  if (!nb) return null;
  const src = sourceOf(nb.node);
  const SrcIcon = SOURCE_ICON[src?.type];
  return (
    <>
      <Column $side="left" aria-label={t('impactPath.neighbourhood.into')}>
        {src && (
          <Source>
            {SrcIcon && <SrcIcon size={14} />}
            <span>
              {t(`impactPath.neighbourhood.source.${src.type || 'person'}`)}
              {src.document ? ` · ${src.document}` : ''}{src.date ? ` · ${src.date}` : ''}
            </span>
          </Source>
        )}
        <Heading>{t('impactPath.neighbourhood.into')}</Heading>
        {nb.incoming.length ? nb.incoming.map((e) => <Neighbour key={e.key} entry={e} onOpen={onOpen} t={t} />)
          : <Empty>{t('impactPath.neighbourhood.none')}</Empty>}
      </Column>
      <Column $side="right" aria-label={t('impactPath.neighbourhood.outOf')}>
        <Heading>{t('impactPath.neighbourhood.outOf')}</Heading>
        {nb.outgoing.length ? nb.outgoing.map((e) => <Neighbour key={e.key} entry={e} onOpen={onOpen} t={t} />)
          : <Empty>{t('impactPath.neighbourhood.none')}</Empty>}
      </Column>
    </>
  );
}
